import { createSignal } from 'rwwa-rx-state-machine'
import type { ToteRaceStatusChangedPushEvent } from 'tabtouch-push-contract'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import type {
  BetSlipResponse,
  BetError,
  BetSlipErrorResponse,
  BetSlipItem,
} from '@mobi/betslip/types'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import type {
  ToteBetResponse,
  FobProposeResponse,
  FobCommitResponse,
  FobSingleErrorResponse,
  MysteryProposeResponse,
  MysteryCommitResponse,
} from '@core/Data/betting'
import type { QuickbetState } from './driver'
import type { BonusBet, InvestmentType } from './Components/BetInvestment/driver'
import type { NotificationType } from './Components/Notifications/types'
import type { QuickbetSelection } from './types'

export interface QuickbetNotificationData {
  type: NotificationType
  title?: string
  subtitle?: string
}

export const SetInvalidInvestmentNotification = createSignal<QuickbetNotificationData>(
  'SetInvalidInvestmentNotification'
)
export const QuickbetLoadSelection = createSignal<QuickbetSelection>('LoadQuickbetSelection')

export const QuickbetLoadMultiSelection = createSignal<
  Pick<QuickbetState, 'selectionMultiLegs'> &
    Pick<QuickbetSelection, 'betSource' | 'betSourceShareOrigin' | 'copiedBetLoopPostId'>
>('LoadQuickbetMultiSelection')

export const QuickbetClosed = createSignal('QuickbetClosed')
export const SetAllowInvestmentState = createSignal<{
  shouldAllowPlaceInvestment: boolean
  shouldAllowWinInvestment: boolean
}>('SetAllowInvestmentState')
export const ChangeInvestment = createSignal<InvestmentType>('ChangeInvestment')
export const SetActiveInvestment = createSignal<InvestmentType>('SetActiveInvestment')
export const ToggleEachWay = createSignal<void>('ToggleEachWay')
export const ToggleBonusBetUsage = createSignal<boolean | undefined>('ToggleBonusBetUsage')
export const ToggleBonusCashUsage = createSignal<boolean | undefined>('ToggleBonusCashUsage')
export const SetBonusBet = createSignal<BonusBet | null>('SetBonusBet')

export const ProposeBet = createSignal<void>('ProposeBet')
export const ProposeBetSucceeded = createSignal<
  ToteBetResponse | FobProposeResponse | MysteryProposeResponse
>('ProposeBetSucceeded')
export const ProposeBetslipBetsSucceeded = createSignal('ProposeBetslipBetsSucceeded')
export const ProposeBetFailed = createSignal<FobSingleErrorResponse>('ProposeBetFailed')

export const ConfirmBet = createSignal<void>('ConfirmBet')
export const ConfirmBetSucceeded = createSignal<
  ToteBetResponse | FobCommitResponse | MysteryCommitResponse
>('ConfirmBetSucceeded')
export const ConfirmBetslipBetsSucceeded = createSignal<BetSlipResponse[]>(
  'ConfirmBetslipBetsSucceeded'
)
export const ConfirmBetFailed = createSignal<FobSingleErrorResponse>('ConfirmBetFailed')
export const ConfirmBetslipBetsFailed = createSignal<BetError[]>('ConfirmBetslipBetFailed')

export const DepositFundsDisplayed = createSignal<void>('DepositFundsDisplayed')
export const AddingToBetslip = createSignal('AddingToBetslip')
export const RaceClosedEventReceived = createSignal<{
  event: ToteRaceStatusChangedPushEvent
  toastId?: string
}>('RaceClosedEventReceived')
export const SetMysteryQuickPickPresetInvestment = createSignal<number>(
  'SetMysteryQuickPickPresetInvestment'
)
export const InsufficientFundsForBet = createSignal<{ betCost: number; shortfall: number }>(
  'InsufficientFundsForBet'
)
export const ClearInsufficientFundsForBet = createSignal('ClearInsufficientFundsForBet')
export const KeepRaceCardSelections = createSignal('KeepRaceCardSelections')

// Multi Only
export const ProposeMultiBetFailed = createSignal<BetSlipErrorResponse>('ProposeMultiBetFailed')
export const ProposeMultiBetSucceeded = createSignal<BetSlipItem[]>('ProposeMultiBetSucceeded')
export const ConfirmMultiBetFailed = createSignal<BetSlipErrorResponse>('ConfirmMultiBetFailed')
export const ConfirmMultiBetSucceeded = createSignal<BetSlipResponse[]>('ConfirmMultiBetSucceeded')
export const MultiBetResponsePriceChange = createSignal<BetSlipItem[]>(
  'MultiBetResponsePriceChange'
)

// Kambi Only
export const ProposeKambiBetFailed = createSignal<KambiBetFailed>('ProposeKambiBetFailed')
export const ProposeKambiBetSucceeded = createSignal('ProposeKambiBetSucceeded')
export const ConfirmKambiBetFailed = createSignal<KambiBetFailed>('ConfirmKambiBetFailed')
export const ConfirmKambiBetSucceeded = createSignal<KambiBetSuccess>('ConfirmKambiBetSucceeded')
export const KambiBetResponsePriceChange = createSignal<KambiPriceChange>('KambiBetPriceChange')
export const SelectKambiReward = createSignal<KambiValidReward | null>('SelectKambiReward')
export const KambiBetResponseRewardNotAvailable = createSignal('KambiBetRewardNotAvailable')

// ===========
// Local Types
// ===========

type KambiPriceChange = { outcomeIds: number[]; odds: number | null }[]
type KambiBetSuccess = { coupon: KambiHistoryCoupon }
type KambiBetFailed =
  | null
  | 'Betting Closed'
  | 'Insufficient Funds'
  | 'Mobi Not Logged In'
  | 'Mixed Live And Prematch'
  | 'Payout Too High'
  | { type: 'Stake Too High'; limit: number }
